import { ACKNOWLEDGE_LUX_PLUS_MONTH_FREE_PREVIEW_MODAL } from 'actions/actionConstants'
import FormatCurrency from 'components/Common/FormatCurrency'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getImageUrl } from 'lib/image/imageUtils'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineUnlockAltIcon from 'LuxKit/Icons/line/LineUnlockAltIcon'
import SolidTagAltIcon from 'LuxKit/Icons/solid/SolidTagAltIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import type { ModalFooterOnClickHandler } from 'LuxKit/Modal/ModalFooter'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import Pill from 'LuxKit/Pill'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useCallback, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

const Hero = styled(Sheet)`
  background:
    url(${getImageUrl('lux-plus-month-plus-texture-desktop')}) no-repeat bottom center / 100%,
    radial-gradient(100% 101.45% at 0% 1.74%, #4866DE 0%, #4054CB 16.37%, #3645BE 24.83%, #2A36B4 33.74%, #202CAE 41.88%, #1923A7 47.59%, #1B1E9B 55%, #202199 61.87%, #171891 69.31%, #12158A 79.48%, #0F1476 89.22%, #0C156E 100%);
`

function LuxPlusMonthFreePreviewInfoModal() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext()
  const dismiss = useCallback<ModalFooterOnClickHandler>(() => {
    modalElement.resolve()
  }, [modalElement])
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(() => subscriptionProduct.offers?.find(offer => !offer.joinFeeWaived), [subscriptionProduct.offers])

  useEffect(() => {
    dispatch({ type: ACKNOWLEDGE_LUX_PLUS_MONTH_FREE_PREVIEW_MODAL })
  }, [dispatch])

  return <ModalBase>
    <ModalBody>
      <Hero padding={20} paletteType="inverse">
        <Group direction="vertical" horizontalAlign="center" gap={12} tabletGap={16}>
          <Pill kind="primary" variant="success">{intl.formatMessage({
            id: '8N0vKFT00VhZRHM3qg_4',
            defaultMessage: '{duration}-day Free Preview',
            description: 'Pill label showing free preview duration for LuxPlus+ Month',
          }, { duration: LUXURY_PLUS.FREE_PREVIEW_DURATION })}
          </Pill>
          <Group direction="vertical" horizontalAlign="center" gap={4} tabletGap={8}>
            <Heading variant="heading2">
              {!!firstName && intl.formatMessage({
                id: 'JGwirKW2hYc0ES0IQGwS',
                defaultMessage: '{firstName}, browse like a VIP during',
                description: 'Personalized heading with user name for LuxPlus+ Month free preview modal',
              }, { firstName })}
              {!firstName && intl.formatMessage({
                id: 'o_0sx1sFD1wxrn9STYks',
                defaultMessage: 'Browse like a VIP during',
                description: 'Heading for LuxPlus+ Month free preview modal when user name is not available',
              })}
            </Heading>
            <Heading variant="heading2">
              <LuxPlusLogo height="0.65em" /> {intl.formatMessage({
                id: '0Vhq8kLE4NmFcZzBETnW',
                defaultMessage: 'Month',
                description: 'Campaign month label in LuxPlus+ Month free preview modal heading',
              })}
            </Heading>
          </Group>
        </Group>
      </Hero>
      <ModalContent>
        <Group direction="vertical" gap={16} tabletGap={24}>
          <Group direction="vertical" gap={12}>
            <BodyText variant="large">
              <FormattedMessage
                id="di-IhB6NL2p0B_3DNcOi"
                defaultMessage="Your <b>{duration}-day free preview</b> is now active. Enjoy VIP perks, hidden deals and early access while you explore."
                description="Body text confirming free preview activation in LuxPlus+ Month modal"
                values={{
                  b: (chunks) => <b>{chunks}</b>,
                  duration: LUXURY_PLUS.FREE_PREVIEW_DURATION,
                }}
              />
            </BodyText>
            {!!offer && <BodyText variant="large">
              <FormattedMessage
                id="VKk2JFnb_y0McYVWxTKy"
                defaultMessage="Unlock unlimited year-round benefits for {price} a year, with a one-time {joinFee} joining fee — including:"
                description="Text describing LuxPlus+ subscription pricing in free preview modal"
                values={{
                  price: <FormatCurrency value={offer.price} />,
                  joinFee: <FormatCurrency value={offer.joinFee} />,
                }}
              />
            </BodyText>}
          </Group>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" startIcon={<LineBedIcon colour="lux-plus" />}>
              {intl.formatMessage({
                id: 'v0CjRXGB1aBxcxTT6YQ3',
                defaultMessage: 'Save hundreds on hotel offers, tours and local experiences every year.',
                description: 'Benefit item about savings on hotel offers in LuxPlus+ Month free preview modal',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineStarIcon colour="lux-plus" />}>
              {intl.formatMessage({
                id: 'xx0YcfiHkLDjAjgPqyU2',
                defaultMessage: 'Bonus inclusions: hotel credit, daily breakfast, upgrades & more on select hotels',
                description: 'Benefit item about bonus inclusions in LuxPlus+ Month free preview modal',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineUnlockAltIcon colour="lux-plus" />}>
              {intl.formatMessage({
                id: 'k7HGBcaoN1sEwRSaEyIr',
                defaultMessage: 'Early access to our bestselling offers plus hidden offers, exclusive to members',
                description: 'Benefit item about early access in LuxPlus+ Month free preview modal',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LinePlusCircleIcon colour="lux-plus" />}>
              <FormattedMessage
                id="5M7-3VURKiVmC_5NfaRW"
                defaultMessage="<plus>PLUS</plus> so much more"
                description="Text highlighting additional benefits beyond those listed in LuxPlus+ Month modal"
                values={{
                  plus: (chunks) => <Colour value="lux-plus">{chunks}</Colour>,
                }}
              />
            </BodyTextBlock>
          </Group>
          {!!offer && <MessageBanner kind="promotional-primary" icon={<SolidTagAltIcon />}>
            <FormattedMessage
              id="lyrFQNZrVekPMzi5vLh2"
              defaultMessage="<b>Special offer: </b> No joining fee when you purchase an eligible {programName} offer. <b>Valued at {joinFee}</b>"
              description="Special offer message about waived joining fee when purchasing LuxPlus+ offer"
              values={{
                b: (chunks) => <b>{chunks}</b>,
                programName: LUXURY_PLUS.PROGRAM_NAME,
                joinFee: <FormatCurrency value={offer.joinFee} />,
              }}
            />
          </MessageBanner>}
        </Group>
      </ModalContent>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'wouTEr2Xyfi_kGjB2eqt',
            defaultMessage: 'Continue to {programName} Month',
            description: 'Button text to continue to LuxPlus+ Month offers from free preview modal',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          onClick: dismiss,
        }}
      />
    </ModalBody>
  </ModalBase>
}

export default LuxPlusMonthFreePreviewInfoModal
